// src/components/Pricing.js
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';

const pricingPlans = [
  {
    name: "DataCenter",
    price: "1",
    duration: "week",
    description: "Data Center Proxies Plan",
    features: [
      "Unlimited Traffic",
      "High Speeds",
      "Fixed Location",
      "Shared and Dedicated",
      "HTTP/SOCKS5 Support"
    ],
    color: "from-blue-500/20 to-transparent",
    url: "https://datacenter.nexaproxy.com/"
  },
  {
    name: "Residential",
    price: "6",
    duration: "month",
    description: "Residential Proxies Plan",
    features: [
      "Unlimited Traffic",
      "No Blacklists",
      "Dedicated",
      "Fixed Location",
      "Advanced IP Rotation"
    ],
    color: "from-purple-500/20 to-transparent",
    isPopular: true,
    url: "https://residential.nexaproxy.com"
  },
  {
    name: "Elite Residential",
    price: "10",
    duration: "GB",
    description: "Premium Residential Proxies",
    features: [
      "Zero Captchas Or Blocking",
      "Immunity From IP Bans",
      "Use For PayPal, Outlier, Amazon",
      "Multiple Locations Per Order",
      "Guaranteed Performance"
    ],
    color: "from-accent/20 to-transparent",
    url: "https://elite.nexaproxy.com"
  }
];

const PricingCard = ({ plan, onSelect, isRedirecting, selectedPlan }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      className="relative group w-full max-w-sm"
    >
      {plan.isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-accent text-dark-bg px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}

      <div className={`glass-effect p-6 md:p-8 rounded-2xl ${
        plan.isPopular ? 'border-accent' : 'border-gray-700'
      } hover:border-accent/50 transition-all duration-300`}>
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-xl md:text-2xl font-bold mb-2">{plan.name}</h3>
          <div className="flex items-center justify-center mb-2">
            <span className="text-sm text-text-secondary">$</span>
            <span className="text-4xl font-bold mx-1">{plan.price}</span>
            <span className="text-text-secondary">/{plan.duration}</span>
          </div>
          <p className="text-text-secondary text-sm">{plan.description}</p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          {plan.features.map((feature, index) => (
            <div key={index} className="flex items-center">
              <svg
                className="w-5 h-5 text-accent mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-text-secondary text-sm">{feature}</span>
            </div>
          ))}
        </div>

        {/* Button */}
        <button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSelect(plan)}
          disabled={isRedirecting}
          className={`w-full py-3 rounded-xl font-medium transition-all duration-300 
            ${plan.isPopular
              ? 'bg-accent hover:bg-accent-hover text-dark-bg'
              : 'border border-accent text-accent hover:bg-accent/10'
            }
            ${isRedirecting && selectedPlan?.name !== plan.name ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className="flex items-center justify-center space-x-2">
            {isRedirecting && selectedPlan?.name === plan.name ? (
              <>
                <span>Redirecting</span>
                <svg 
                  className="animate-spin h-5 w-5" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </>
            ) : (
              'Get Started'
            )}
          </div>
        </button>
      </div>

      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl -z-10">
        <div className={`absolute inset-0 bg-gradient-to-br ${plan.color}`} />
      </div>
    </div>
  );
};
const Pricing = ({ showNotification }) => {
    const [ref, inView] = useInView({
      threshold: 0.1,
      triggerOnce: true
    });
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
  
    const handlePlanSelect = (plan) => {
      setIsRedirecting(true);
      setSelectedPlan(plan);
      
      showNotification(`Redirecting to ${plan.name} portal...`, 'success');
      
      setTimeout(() => {
        window.location.href = plan.url;
      }, 500);
    };
  
    const scrollToContact = () => {
      document.getElementById('contact')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    };
  
    return (
      <section className="py-16 md:py-24 relative" id="pricing" ref={ref}>
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-1/4 w-1/2 h-1/2 bg-accent/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 left-1/4 w-1/2 h-1/2 bg-purple-500/5 rounded-full blur-[120px]" />
        </div>
  
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Simple, Transparent <span className="text-accent">Pricing</span>
            </h2>
            <p className="text-text-secondary text-sm md:text-base max-w-2xl mx-auto">
              Choose the perfect plan for your needs. All plans include our core features.
            </p>
          </div>
  
          {/* Pricing Grid - Centered */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl">
              {pricingPlans.map((plan, index) => (
                <PricingCard
                  key={index}
                  plan={plan}
                  onSelect={handlePlanSelect}
                  isRedirecting={isRedirecting}
                  selectedPlan={selectedPlan}
                />
              ))}
            </div>
          </div>
  
          {/* Bottom Note */}
          <div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12 md:mt-20 relative z-10"
          >
            <button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
              className="text-accent hover:text-accent-hover transition-colors inline-flex items-center space-x-2 cursor-pointer"
            >
              <span>Need a custom plan? Contact us for more information and requirements</span>
              <svg 
                className="w-4 h-4 transform transition-transform group-hover:translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>
    );
  };
  
  export default Pricing;